import React, { useState, useEffect } from 'react';
import { formatMontant, getDeviseCourante } from '../../utils/format';
import { AlertTriangle, CheckCircle, MessageSquare, Plus, Trash2 } from 'lucide-react';
import api from '../../services/api';
import { Button, Select } from '../../components/common/Input';
import InputMontant from '../../components/common/InputMontant';
import toast from 'react-hot-toast';

// ──────────────────────────────────────────────────────────────
// FormRetourCommission — clôture d'une sortie en MODE COMMISSION.
//
// Pour chaque article confié, le gérant saisit les ventes réelles. Le prix
// réel pouvant varier d'une unité à l'autre, chaque article peut avoir
// plusieurs « lignes de vente » : quantité, prix réel, commission/unité.
//   - Bénéfice/unité = prix réel − coût d'achat (affiché comme repère)
//   - Commission/unité plafonnée au bénéfice (garde-fou)
//   - Le vendeur remet 100 % des ventes réelles ; la commission lui est
//     versée le soir (dépense caisse, gérée côté serveur).
//
// Endpoint dédié : POST /vendeurs/sorties/:id/retour-commission.
// Le mode standard passe par FormRetourSoir, inchangé.
// ──────────────────────────────────────────────────────────────
export default function FormRetourCommission({ sortie, sortieDetail, onSuccess, onCancel }) {
  // articles : par ligne de sortie, une liste de sous-ventes { quantite, prix, commission }
  const [articles, setArticles] = useState([]);
  const [montantRemis, setMontantRemis] = useState('');
  const [modePaiement, setModePaiement] = useState('especes');
  const [explication, setExplication]   = useState('');
  const [notes, setNotes]               = useState('');
  const [loading, setLoading]           = useState(false);

  useEffect(() => {
    setArticles((sortieDetail?.lignes || []).map(l => ({
      sortie_ligne_id: l.id,
      variante_id: l.variante_id,
      produit_nom: l.produit_nom,
      modele: l.modele,
      taille: l.taille || '',
      couleur: l.couleur,
      quantite_confiee: parseInt(l.quantite_confiee),
      prix_sortie: parseFloat(l.prix_vente_unitaire),      // prix décidé à la sortie (référence)
      cout_unitaire: parseFloat(l.cout_unitaire) || 0,     // coût d'achat snapshoté
      // 1re sous-vente par défaut : tout invendu (quantité 0), prix réel = prix de sortie
      sousVentes: [{ quantite: '', prix_reel: String(parseFloat(l.prix_vente_unitaire) || 0), commission: '' }],
    })));
  }, [sortieDetail]);

  // ── Helpers d'édition ──────────────────────────────────────────
  const majSousVente = (ai, si, champ, val) => {
    setArticles(prev => prev.map((a, i) => {
      if (i !== ai) return a;
      const sv = a.sousVentes.map((s, j) => j === si ? { ...s, [champ]: val } : s);
      return { ...a, sousVentes: sv };
    }));
  };
  const ajouterSousVente = (ai) => {
    setArticles(prev => prev.map((a, i) => i === ai
      ? { ...a, sousVentes: [...a.sousVentes, { quantite: '', prix_reel: String(a.prix_sortie), commission: '' }] }
      : a));
  };
  const supprimerSousVente = (ai, si) => {
    setArticles(prev => prev.map((a, i) => i === ai
      ? { ...a, sousVentes: a.sousVentes.filter((_, j) => j !== si) }
      : a));
  };

  // ── Calculs temps réel ─────────────────────────────────────────
  let montantAttenduReel = 0;   // total ventes réelles (à encaisser)
  let commissionTotale   = 0;   // total commission à verser
  let totalVendu = 0;
  let depassementBenefice = false;   // au moins une commission > bénéfice
  let survente = false;              // vendu > confié sur un article

  for (const a of articles) {
    let venduArticle = 0;
    for (const s of a.sousVentes) {
      const q = parseInt(s.quantite) || 0;
      const prix = parseFloat(s.prix_reel) || 0;
      const comm = parseFloat(s.commission) || 0;
      if (q <= 0) continue;
      venduArticle += q;
      montantAttenduReel += q * prix;
      commissionTotale   += q * comm;
      if (comm > (prix - a.cout_unitaire)) depassementBenefice = true;
    }
    if (venduArticle > a.quantite_confiee) survente = true;
    totalVendu += venduArticle;
  }

  const montantRemisNum = parseFloat(montantRemis) || 0;
  const aSaisiMontant   = montantRemis !== '' && !isNaN(montantRemisNum);
  const ecart           = montantRemisNum - montantAttenduReel; // remis − ventes réelles

  const ecartPositif        = ecart > 0;
  const ecartNegatifNonExpl = ecart < 0 && !explication.trim();
  const submitBloque = !aSaisiMontant || ecartPositif || ecartNegatifNonExpl
                     || depassementBenefice || survente;

  const handleSubmit = async () => {
    if (submitBloque) return;
    setLoading(true);
    try {
      const ventes = articles
        .map(a => ({
          sortie_ligne_id: a.sortie_ligne_id,
          variante_id: a.variante_id,
          sous_lignes: a.sousVentes
            .filter(s => (parseInt(s.quantite) || 0) > 0)
            .map(s => ({
              quantite: parseInt(s.quantite),
              prix_reel_unitaire: parseFloat(s.prix_reel) || 0,
              commission_unitaire: parseFloat(s.commission) || 0,
            })),
        }))
        .filter(a => a.sous_lignes.length > 0);

      const res = await api.post(`/vendeurs/sorties/${sortie.id}/retour-commission`, {
        ventes,
        montant_remis: montantRemisNum,
        mode_paiement_retour: modePaiement,
        explication_ecart: explication || null,
        notes: notes || null,
      });
      if (res.data.dette_ajoutee > 0) {
        toast(`Écart de ${formatMontant(Math.abs(ecart))} ajouté à la dette de ${sortie.prenom}`, {
          duration: 5000, icon: '⚠️',
        });
      }
      toast.success(`Commission versée : ${formatMontant(commissionTotale)}`);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur serveur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* En-tête */}
      <div className="bg-gray-800 rounded-xl p-4">
        <p className="text-white font-semibold">
          {sortie.prenom} {sortie.nom} — Retour du soir
          <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-600/40">
            Commission
          </span>
        </p>
        <p className="text-gray-400 text-sm">
          {sortieDetail.sortie?.date} · {articles.length} article(s) confié(s)
        </p>
      </div>

      {/* Saisie par article */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-yellow-400 uppercase tracking-wider">
          Ventes réelles &amp; commissions
        </h3>

        {articles.map((a, ai) => {
          const vendu = a.sousVentes.reduce((s, x) => s + (parseInt(x.quantite) || 0), 0);
          const trop = vendu > a.quantite_confiee;
          return (
            <div key={ai} className="bg-gray-800 rounded-xl p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">{a.produit_nom}</p>
                  <p className="text-gray-500 text-xs">
                    {a.modele} {a.taille ? `· ${a.taille}` : ''} · {a.couleur}
                  </p>
                  <p className="text-gray-500 text-xs">
                    Confié : <span className="text-yellow-400">{a.quantite_confiee}</span> ·
                    Prix sortie : {formatMontant(a.prix_sortie)} ·
                    Coût : {formatMontant(a.cout_unitaire)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">Vendu</p>
                  <p className={`font-bold text-sm ${trop ? 'text-red-400' : vendu > 0 ? 'text-green-400' : 'text-gray-600'}`}>
                    {vendu}/{a.quantite_confiee}
                  </p>
                </div>
              </div>

              {/* Sous-ventes (une par prix réel distinct) */}
              <div className="space-y-2">
                {a.sousVentes.map((s, si) => {
                  const q    = parseInt(s.quantite) || 0;
                  const prix = parseFloat(s.prix_reel) || 0;
                  const comm = parseFloat(s.commission) || 0;
                  const benef = prix - a.cout_unitaire;
                  const commTrop = comm > benef;
                  return (
                    <div key={si} className="bg-gray-900/60 rounded-lg p-2">
                      <div className="grid grid-cols-4 gap-2 items-start">
                        <div>
                          <label className="block text-[10px] text-gray-500 mb-0.5">Qté</label>
                          <input
                            type="number" min="0" value={s.quantite}
                            onChange={e => majSousVente(ai, si, 'quantite', e.target.value)}
                            placeholder="0"
                            className="w-full bg-gray-700 border border-gray-600 text-white rounded px-2 py-1.5 text-sm text-center focus:outline-none focus:border-yellow-500"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-500 mb-0.5">Prix réel</label>
                          <InputMontant
                            value={s.prix_reel}
                            onChange={v => majSousVente(ai, si, 'prix_reel', v)}
                            devise={null}
                            className="w-full bg-gray-700 border border-gray-600 text-white rounded px-2 py-1.5 text-sm text-right focus:outline-none focus:border-yellow-500"
                          />
                          <p className="text-[10px] text-green-400/90 mt-0.5 text-right">
                            {q > 0 ? `= ${formatMontant(q * prix)}` : ' '}
                          </p>
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-500 mb-0.5">Bénéfice/u</label>
                          <p className={`px-1 py-1.5 text-sm text-right font-medium ${benef < 0 ? 'text-red-400' : 'text-blue-300'}`}>
                            {formatMontant(benef)}
                          </p>
                          <p className="text-[10px] text-blue-300/80 mt-0.5 text-right">
                            {q > 0 ? `= ${formatMontant(q * benef)}` : ' '}
                          </p>
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-500 mb-0.5">Commission/u</label>
                          <InputMontant
                            value={s.commission}
                            onChange={v => majSousVente(ai, si, 'commission', v)}
                            devise={null}
                            placeholder="0"
                            className={`w-full bg-gray-700 border text-white rounded px-2 py-1.5 text-sm text-right focus:outline-none ${
                              commTrop ? 'border-red-600 focus:border-red-500' : 'border-gray-600 focus:border-yellow-500'
                            }`}
                          />
                          <p className="text-[10px] text-purple-300/90 mt-0.5 text-right">
                            {q > 0 ? `= ${formatMontant(q * comm)}` : ' '}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        {commTrop
                          ? <span className="text-[10px] text-red-400">Commission &gt; bénéfice</span>
                          : <span className="text-[10px] text-gray-600"> </span>}
                        {a.sousVentes.length > 1 && (
                          <button onClick={() => supprimerSousVente(ai, si)} className="text-gray-500 hover:text-red-400">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                <button
                  onClick={() => ajouterSousVente(ai)}
                  className="inline-flex items-center gap-1 text-xs text-yellow-400 hover:text-yellow-300"
                >
                  <Plus size={13} /> Prix différent pour d'autres unités
                </button>
              </div>
              {trop && (
                <p className="text-red-400 text-xs">La quantité vendue dépasse la quantité confiée.</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Récapitulatif */}
      <div className="bg-gray-800 rounded-xl p-4 space-y-2">
        <h3 className="text-sm font-semibold text-gray-300 mb-3">Récapitulatif</h3>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">Ventes réelles ({totalVendu} pcs) — à encaisser</span>
          <span className="text-green-400 font-bold">{formatMontant(montantAttenduReel)}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">Commission à verser au vendeur</span>
          <span className="text-purple-300 font-medium">{formatMontant(commissionTotale)}</span>
        </div>
      </div>

      {/* Montant remis */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-yellow-400 uppercase tracking-wider">
          Argent remis par le vendeur
        </h3>
        <div>
          <label className="block text-sm text-gray-400 mb-1.5">Montant remis ({getDeviseCourante()}) *</label>
          <div className="relative">
            <InputMontant
              value={montantRemis}
              onChange={setMontantRemis}
              devise={null}
              placeholder={`À encaisser : ${montantAttenduReel.toLocaleString()} ${getDeviseCourante()}`}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 pr-14 text-lg font-bold focus:outline-none focus:border-yellow-500"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-semibold pointer-events-none">{getDeviseCourante()}</span>
          </div>
          <p className="text-[11px] text-gray-500 mt-1">
            Le vendeur remet la totalité des ventes ; sa commission lui est versée à part.
          </p>
        </div>
        <Select label="Mode de paiement" value={modePaiement} onChange={e => setModePaiement(e.target.value)}>
          <option value="especes">💵 Espèces</option>
          <option value="orange_money">🟠 Orange Money</option>
          <option value="mtn_money">🟡 MTN Money</option>
          <option value="mixte">🔀 Mixte</option>
        </Select>

        {aSaisiMontant && (
          <div className={`rounded-xl p-4 ${
            ecart === 0 ? 'bg-green-900/20 border border-green-700/30'
            : ecart < 0 ? 'bg-orange-900/30 border border-orange-700/50'
            : 'bg-red-900/40 border border-red-700/60'
          }`}>
            <div className="flex items-start gap-2">
              {ecart === 0
                ? <CheckCircle size={18} className="text-green-400 flex-shrink-0 mt-0.5" />
                : <AlertTriangle size={18} className={`flex-shrink-0 mt-0.5 ${ecart < 0 ? 'text-orange-400' : 'text-red-400'}`} />}
              <div className="flex-1">
                <p className={`font-bold ${ecart === 0 ? 'text-green-400' : ecart < 0 ? 'text-orange-300' : 'text-red-300'}`}>
                  Écart : {ecart >= 0 ? '+' : ''}{formatMontant(ecart)}
                </p>
                {ecart < 0 && (
                  <p className="text-orange-200 text-xs mt-0.5">
                    Le montant remis est inférieur aux ventes réelles. Le manque ira à la dette de {sortie.prenom}.
                    <strong className="block mt-1">Explication obligatoire ci-dessous.</strong>
                  </p>
                )}
                {ecart > 0 && (
                  <p className="text-red-200 text-xs mt-0.5">
                    Le montant remis dépasse les ventes réelles. Vérifie les quantités et prix saisis.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {aSaisiMontant && ecart < 0 && (
          <div>
            <label className="text-sm text-gray-400 mb-1.5 flex items-center gap-1.5">
              <MessageSquare size={14} className="text-orange-400" />
              Explication de l'écart <span className="text-red-400">*</span>
            </label>
            <textarea
              value={explication}
              onChange={e => setExplication(e.target.value)}
              placeholder="Ex : oubli, perte, vente à crédit non collectée…"
              rows={2}
              className={`w-full bg-gray-800 border text-white rounded-lg px-3 py-2 text-sm focus:outline-none ${
                !explication.trim() ? 'border-red-700/60 focus:border-red-500' : 'border-gray-700 focus:border-yellow-500'
              }`}
            />
          </div>
        )}

        <div>
          <label className="block text-sm text-gray-400 mb-1.5">Notes (libre)</label>
          <input
            value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Notes optionnelles…"
            className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-500"
          />
        </div>
      </div>

      {depassementBenefice && (
        <p className="text-red-400 text-xs">
          Une commission dépasse le bénéfice d'un article : corrige avant de valider.
        </p>
      )}

      <div className="flex space-x-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel} className="flex-1">Annuler</Button>
        <Button
          loading={loading}
          onClick={handleSubmit}
          disabled={submitBloque}
          className="flex-1"
          variant={ecart < 0 ? 'danger' : 'primary'}
        >
          {ecart < 0 ? '⚠️ Enregistrer avec écart'
            : (ecartPositif || depassementBenefice || survente) ? '⛔ Saisie invalide'
            : 'Enregistrer le retour'}
        </Button>
      </div>
    </div>
  );
}
